import {
  BtnLayerType,
  CancelSubscriptionLayerType,
  EcommercePropsType,
  FormLayerType,
  OrderConfirmationLayerType,
  OrderItemType,
  PageLayerType,
} from './types';
import { CURRENCY_SYMBOLS } from '@global/App/constants/location/constants';
import { SiteConfig } from '../SiteConfig';
import { Pages } from '@src/global/App/constants/constants';
import { getUserInfoFromTokens } from '../getUserInfoFromTokens';

interface Login {
  isLoggedIn?: boolean;
  loginId?: string;
}

const PAGES = {
  [Pages.CHECKOUT.slice(1)]: 'checkout',
};

class AdobeAnalytics {
  isWork: boolean;

  constructor() {
    this.isWork = true;

    this.dataLayerOrderConfirmation =
      this.dataLayerOrderConfirmation.bind(this);

    this.dataLayerForm = this.dataLayerForm.bind(this);

    this.dataLayerPageLoaded = this.dataLayerPageLoaded.bind(this);

    this.dataLayerBtnTracker = this.dataLayerBtnTracker.bind(this);

    this.dataLayerCheckOutPageLoad = this.dataLayerCheckOutPageLoad.bind(this);
  }

  private buildProduct({
    maId,
    maName,
    category,
    priceBeforeDiscount,
    qty,
    parentId,
    parentCategoryName,
    tax,
  }: OrderItemType) {
    const product = {
      sku: maId?.toLowerCase() || '',
      category: parentCategoryName?.toLowerCase() || 'ecommerce',
      productName: maName?.toLowerCase() || '',
      productId: parentId?.toLowerCase() || '',
      type: category?.toLowerCase() || '',
      source: SiteConfig.getConfig().siteCode?.toLowerCase() || '',
    };
    if (priceBeforeDiscount) {
      const priceOfOneQty = priceBeforeDiscount / qty;
      return {
        ...product,
        quantity: qty?.toString(),
        price: priceOfOneQty?.toFixed(2)?.toString(),
        totalRevenue: priceBeforeDiscount?.toFixed(2)?.toString(),
        productTax: tax?.value?.toFixed(2)?.toString() || '0.00',
      };
    }
    return product;
  }
  private detectPlatform = (): 'web' | 'ios' | 'android' => {
    const userAgent = window.navigator.userAgent?.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    }
    if (/android/.test(userAgent)) {
      return 'android';
    }
    return 'web';
  };

  private buildProducts(products: OrderItemType[]) {
    return products.map((orderItem) => this.buildProduct(orderItem));
  }

  private buildPageLayer(props: PageLayerType & Login) {
    const { page, event, error, isLoggedIn } = props;
    const { hostname } = window.location;
    const { lang, iso: countryName } = SiteConfig.getConfig();
    const localeStr = `${countryName?.toLowerCase()}-${lang?.toLowerCase()}`;
    const userInfo = getUserInfoFromTokens();

    const getSimplePageName = (name: string): string => {
      if (name === 'checkout:order-confirmation') return 'order-confirmation';
      if (name.startsWith('checkout:')) return 'checkout';
      return name;
    };

    const adobeDataObj = {
      event: event ?? 'pageView',

      page: {
        pageName: getSimplePageName(page.name),
        section: page.section?.toLowerCase() || '',
        subSection: page.subSection?.toLowerCase() || '',
        subSubSection: '',
        pageCategory: 'ecommerce',
        pageLanguage: lang?.toLowerCase() || '',
        pageTemplate: 'checkout-page',
        pageBuild: 'react',
        pageNumber: '',
        breadcrumb: '',
      },

      site: {
        name: hostname.toLowerCase(),
        platform: this.detectPlatform(),
        locale: localeStr,
      },
      user: {
        loginStatus: isLoggedIn ? 'logged-in' : 'not-logged-in',
        loginType: 'account',
        adminType: userInfo.adminType?.toLowerCase() || '',
        almId: userInfo.almId?.toLowerCase() || '',
        connectId: userInfo.connectId?.toLowerCase() || '',
      },
    };

    if (error) {
      return {
        ...adobeDataObj,
        error: {
          serverErrorCode: `${error.code}`,
          errorMessage: error.message?.toLowerCase() || '',
        },
      };
    }
    return adobeDataObj;
  }

  private buildEcommerceLayer(props: EcommercePropsType) {
    const { orderItems, currency } = props;
    return {
      currency: currency?.toLowerCase() ?? '',
      products: [...this.buildProducts(orderItems)],
    };
  }

  private buildFullEcommerceLayer(props: EcommercePropsType & Login) {
    const {
      orderId,
      orderPrice,
      promotions,
      paymentType,
      deliveryOption,
      currency,
      isLoggedIn,
    } = props;
    const currencySymbol = CURRENCY_SYMBOLS[currency];
    const eCommerceObject = {
      checkoutType: isLoggedIn ? 'member' : 'guest',
      orderId: orderId?.toLowerCase() ?? undefined,
      paymentMethod: paymentType?.toLowerCase(),
      totalRevenue: orderPrice?.total?.value?.toFixed(2)?.toString(),
      subTotalRevenue: orderPrice?.price?.value?.toFixed(2)?.toString(),
      totalTax: orderPrice?.tax?.value?.toFixed(2)?.toString(),
      ...this.buildEcommerceLayer(props), // the total tax + the shipping tax
      shippingMethod: deliveryOption?.title?.toLowerCase() || '',
      shippingTotal: deliveryOption?.price
        ? deliveryOption?.price?.replace(currencySymbol, '')
        : '',
      promoCode: promotions?.[0]?.prmId?.toLowerCase() ?? '',
      promoCodeDiscount:
        orderPrice?.discount?.value?.toFixed(2)?.toString() ?? '',
    };
    return eCommerceObject;
  }

  private buildOrderConfirmation(props: OrderConfirmationLayerType & Login) {
    const { orderPrice } = props;
    const ecommerceLayer = this.buildFullEcommerceLayer({
      ...props,
      currency: orderPrice?.total.units,
    });
    return {
      event: 'orderConfirmation',
      ecommerce: ecommerceLayer,
    };
  }

  private parseUrlPath() {
    const url = location.pathname;

    const language = url.slice(1, 6);

    const pageSide = url.slice(7);
    const slashIndex = pageSide.indexOf('/');
    const page = slashIndex != -1 ? pageSide.slice(0) : pageSide;

    return {
      pageName: PAGES[page] || page,
      language,
    };
  }

  dataLayerOrderConfirmation(props: OrderConfirmationLayerType & Login) {
    if (!this.isWork) return;

    // OLD: Push pageView first, then orderConfirmation (TSD requirement #17)
    // NEW: Push orderConfirmation first, then pageView
    const page = {
      section: 'order confirmation',
      name: 'checkout:order-confirmation',
      subSection: '',
    };
    const adobeDataObj = this.buildOrderConfirmation(props);
    window.adobeDataLayer.push(adobeDataObj);

    const { isLoggedIn, loginId } = props;
    const event = 'pageView';
    const pageLayer = this.buildPageLayer({
      page,
      event,
      isLoggedIn,
      loginId,
    });
    window.adobeDataLayer.push(pageLayer);
  }

  dataLayerForm(props: FormLayerType & Login) {
    if (!this.isWork) return;

    const { pageName, language } = this.parseUrlPath();
    const { formName, formEvent, errorMsg } = props;

    const fullLang = language.replace('-', '');

    const adobeDataObj = {
      event: `${formEvent}`,
      formName: `wil:form:${fullLang}:${formName}:${pageName}`,
      errorMsg: errorMsg ? `${errorMsg}` : null,
    };
    window.adobeDataLayer.push(adobeDataObj);
  }

  dataLayerPageLoaded(props: PageLayerType & Login) {
    if (!this.isWork) return;

    const adobeDataObj = this.buildPageLayer(props);
    window.adobeDataLayer.push(adobeDataObj);
  }

  dataLayerBtnTracker(props: BtnLayerType) {
    if (!this.isWork) return;

    const { btnType, btnName } = props;
    const adobeDataObj = {
      event: 'button click',
      click: {
        type: `${btnType}`,
        name: `${btnName}`,
      },
    };
    window.adobeDataLayer.push(adobeDataObj);
  }

  dataLayerSchoolInfoClick() {
    if (!this.isWork) return;
    const adobeDataObj = {
      event: 'checkoutSchool',
      component: {
        componentType: 'button',
        componentText: 'save and continue',
        componentProductCode: '',
        componentPosition: '',
        linkURL: '',
        clickCategory: 'cta',
        parentComponentType: 'checkout step',
        parentComponentText: 'school information',
      },
    };
    window.adobeDataLayer.push(adobeDataObj);
  }

  dataLayerPlaceOrderClick() {
    if (!this.isWork) return;
    const adobeDataObj = {
      event: 'checkoutOrderReview',
      component: {
        componentType: 'button',
        componentText: 'place order',
        componentProductCode: '',
        componentPosition: '',
        linkURL: '',
        clickCategory: 'cta',
        parentComponentType: 'checkout step',
        parentComponentText: 'order review',
      },
    };
    window.adobeDataLayer.push(adobeDataObj);
  }

  dataLayerCheckOutShipping() {
    if (!this.isWork) return;
    const adobeDataObj = {
      event: 'checkoutShipping',
      component: {
        componentType: 'button',
        componentText: 'save and continue',
        componentProductCode: '',
        componentPosition: '',
        linkURL: '',
        clickCategory: 'cta',
        parentComponentType: 'checkout step',
        parentComponentText: 'shipping',
      },
    };
    window.adobeDataLayer.push(adobeDataObj);
  }

  dataLayerCheckOutPaymentBilling() {
    if (!this.isWork) return;
    const adobeDataObj = {
      event: 'checkoutPaymentBilling',
      component: {
        componentType: 'button',
        componentText: 'save and continue',
        componentProductCode: '',
        componentPosition: '',
        linkURL: '',
        clickCategory: 'cta',
        parentComponentType: 'checkout step',
        parentComponentText: 'payment and billing address',
      },
    };
    window.adobeDataLayer.push(adobeDataObj);
  }

  dataLayerCheckoutAccountClick() {
    if (!this.isWork) return;
    const adobeDataObj = {
      event: 'checkoutAccount',
      component: {
        componentType: 'button',
        componentText: 'account',
        componentProductCode: '',
        componentPosition: '',
        linkURL: '',
        clickCategory: 'cta',
        parentComponentType: 'checkout step',
        parentComponentText: 'account',
      },
    };
    window.adobeDataLayer.push(adobeDataObj);
  }

  dataLayerCheckOutPageLoad() {
    if (!this.isWork) return;
    this.dataLayerCheckoutAccountClick();
  }

  dataLayerCancelSubscriptionTracker(props: CancelSubscriptionLayerType) {
    if (!this.isWork) return;

    const { product } = props;
    const adobeDataObj = {
      event: 'unsubscribe',
      ecommerce: {
        products: this.buildProduct(product),
      },
    };
    window.adobeDataLayer.push(adobeDataObj);
  }

  dataLayerContinueShoppingClick(buttonText: string, linkURL: string) {
    if (!this.isWork) return;
    const adobeDataObj = {
      event: 'click',
      component: {
        componentType: 'button',
        componentText: buttonText?.toLowerCase() || '',
        componentProductCode: '',
        componentPosition: '',
        linkURL: linkURL || '',
        clickCategory: 'cta',
        parentComponentType: 'order confirmation page',
        parentComponentText: 'order confirmation',
      },
    };
    window.adobeDataLayer.push(adobeDataObj);
  }
}

export default new AdobeAnalytics();
